import React from 'react';
import {
  FaTimes,
  FaDownload,
  FaWaveSquare,
  FaBoxOpen,
  FaBuilding,
  FaDollarSign,
  FaFileAlt,
} from 'react-icons/fa';
import { decodeMovementMeta } from './StockRecordForm';
import './StockRecordForms.css';

function formatMoney(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
}

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function typeLabel(type) {
  if (type === 'IN') return 'IN ↑';
  if (type === 'OUT') return 'OUT ↓';
  if (type === 'TRF') return 'TRF';
  if (type === 'ADJ') return 'ADJ';
  return type || '—';
}

export default function MovementDetailModal({ row, onClose, onEdit }) {
  if (!row) return null;
  const { meta, note } = decodeMovementMeta(row.edit?.remark || row.remark);
  const qtyAbs = Math.abs(Number(row.qty) || Number(row.edit?.quantityValue) || 0);
  const unit = meta.unit || 'Each';
  const unitCost = Number(meta.unitCost) || 0;
  const lineTotal = qtyAbs * unitCost;
  const gst = lineTotal * 0.1;
  const type = row.type || 'IN';
  const from =
    meta.fromSite ||
    meta.supplier ||
    (type === 'IN' ? meta.supplier || '—' : row.site) ||
    '—';
  const to = meta.toSite || meta.site || row.site || '—';
  const remark = note || meta.reason || '—';

  const download = () => {
    const lines = [
      'Inventory History Detail',
      `Movement ID: ${row.code}`,
      `Date: ${formatDate(row.date)}`,
      `Type: ${typeLabel(type)}`,
      `Processed By: ${row.employee || '—'}`,
      `Product: ${row.product}`,
      `SKU: ${row.sku}`,
      `Qty: ${qtyAbs} ${unit}`,
      `Unit Cost: ${unitCost ? formatMoney(unitCost) : '—'}`,
      `From: ${from}`,
      `To: ${to}`,
      `Site: ${meta.site || row.site || '—'}`,
      `PO Reference: ${meta.poNumber || row.reference || '—'}`,
      `Remark: ${remark}`,
      `Subtotal: ${unitCost ? formatMoney(lineTotal) : '—'}`,
      `GST (10%): ${unitCost ? formatMoney(gst) : '—'}`,
      `Total: ${unitCost ? formatMoney(lineTotal + gst) : '—'}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.code || 'movement'}-receipt.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="srd-overlay" onClick={onClose}>
      <div className="srd-modal" onClick={(e) => e.stopPropagation()}>
        <header className="srd-header">
          <h2>Inventory History Detail</h2>
          <button type="button" className="srd-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <div className="srd-body">
          <div className="srd-section">
            <h3><FaWaveSquare /> Movement Summary</h3>
            <div className="srd-kv">
              <div><b>Movement ID</b>{row.code}</div>
              <div><b>Date</b>{formatDate(row.date)}</div>
              <div>
                <b>Type</b>
                <span className={`srd-badge srd-badge--${String(type).toLowerCase()}`}>
                  {typeLabel(type)}
                </span>
              </div>
              <div><b>Processed By</b>{row.employee || '—'}</div>
            </div>
          </div>

          <div className="srd-section">
            <h3><FaBoxOpen /> Items</h3>
            <table className="srd-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Unit Cost</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{row.product}</td>
                  <td>{row.sku || '—'}</td>
                  <td>{qtyAbs}</td>
                  <td>{unit}</td>
                  <td>{unitCost ? formatMoney(unitCost) : '—'}</td>
                  <td>{unitCost ? formatMoney(lineTotal) : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="srd-section">
            <h3><FaBuilding /> Destination</h3>
            <div className="srd-kv">
              <div><b>From</b>{from}</div>
              <div><b>To</b>{to}</div>
              <div><b>Site</b>{meta.site || (type === 'TRF' ? to : row.site) || '—'}</div>
            </div>
          </div>

          <div className="srd-section">
            <h3><FaDollarSign /> Financial</h3>
            <div className="srd-kv">
              <div><b>Subtotal</b>{unitCost ? formatMoney(lineTotal) : '—'}</div>
              <div><b>GST (10%)</b>{unitCost ? formatMoney(gst) : '—'}</div>
              <div><b>Total</b>{unitCost ? formatMoney(lineTotal + gst) : '—'}</div>
            </div>
          </div>

          <div className="srd-section">
            <h3><FaFileAlt /> Notes</h3>
            <div className="srd-kv">
              <div><b>PO Reference</b>{meta.poNumber || row.reference || '—'}</div>
              <div><b>Remark</b>{remark}</div>
            </div>
          </div>

          <div className="srd-footer">
            <button type="button" className="srd-btn" onClick={onClose}>Close</button>
            <button type="button" className="srd-btn" onClick={download}>
              <FaDownload /> Download Receipt
            </button>
            {onEdit ? (
              <button
                type="button"
                className="srd-btn srd-btn--primary"
                onClick={() => onEdit(row)}
              >
                Edit Movement
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
